import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { 
  Users, 
  UserPlus, 
  Clock, 
  CheckCircle, 
  X,
  Loader2,
  Lock
} from 'lucide-react';

export function ConnectionsCard() {
  const { user, hasLicense } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [connections, setConnections] = useState<any[]>([]);
  const [pendingRequests, setPendingRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchConnections();
    }
  }, [user]);

  const fetchConnections = async () => {
    try {
      // Fetch accepted connections
      const { data: acceptedData, error: acceptedError } = await supabase
        .from('profile_connections')
        .select(`
          *,
          requester:profiles!profile_connections_requester_id_fkey(
            id,
            full_name,
            title,
            avatar_url
          ),
          receiver:profiles!profile_connections_receiver_id_fkey(
            id,
            full_name,
            title,
            avatar_url
          )
        `)
        .or(`requester_id.eq.${user?.id},receiver_id.eq.${user?.id}`)
        .eq('status', 'accepted')
        .order('created_at', { ascending: false })
        .limit(5);

      if (acceptedError) throw acceptedError;

      // Fetch pending requests
      const { data: pendingData, error: pendingError } = await supabase
        .from('profile_connections')
        .select(`
          *,
          requester:profiles!profile_connections_requester_id_fkey(
            id,
            full_name,
            title,
            avatar_url
          )
        `)
        .eq('receiver_id', user?.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (pendingError) throw pendingError;

      setConnections(acceptedData || []);
      setPendingRequests(pendingData || []);
    } catch (error) {
      console.error('Error fetching connections:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load connections'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRequest = async (requestId: string, accept: boolean) => {
    try {
      const { error } = await supabase
        .from('profile_connections')
        .update({ 
          status: accept ? 'accepted' : 'rejected',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;

      toast({
        title: accept ? 'Connection Accepted' : 'Connection Declined',
        description: accept ? 'You are now connected' : 'Connection request declined'
      });

      fetchConnections();
    } catch (error) {
      console.error('Error handling connection request:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to process connection request'
      });
    }
  };

  const getConnectionUser = (connection: any) => {
    // Return the other user in the connection
    return connection.requester_id === user?.id ? connection.receiver : connection.requester;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <CardTitle>Professional Network</CardTitle>
          </div>
          {pendingRequests.length > 0 && (
            <Badge variant="secondary" className="gap-1">
              <Clock className="h-3 w-3" />
              {pendingRequests.length} pending
            </Badge>
          )}
        </div>
        <CardDescription>
          Your professional connections and pending requests
        </CardDescription>
      </CardHeader>
      <CardContent>
        {pendingRequests.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-medium mb-3">Pending Requests</h4>
            <div className="space-y-3">
              {pendingRequests.map((request) => (
                <div key={request.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarImage src={request.requester.avatar_url} />
                      <AvatarFallback>
                        {request.requester.full_name?.[0] || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{request.requester.full_name}</p>
                      <p className="text-sm text-muted-foreground">{request.requester.title}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleRequest(request.id, false)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm"
                      onClick={() => handleRequest(request.id, true)}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Accept
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4">
          {connections.length === 0 ? (
            <div className="text-center py-6">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <h3 className="text-lg font-medium mb-2">No Connections Yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Start building your professional network
              </p>
              {hasLicense('pro') ? (
                <Button onClick={() => navigate('/search')}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Find Connections
                </Button>
              ) : (
                <Button onClick={() => navigate('/pricing')}>
                  <Lock className="h-4 w-4 mr-2" />
                  Upgrade to Pro
                </Button>
              )}
            </div>
          ) : (
            <>
              {connections.map((connection) => {
                const connectionUser = getConnectionUser(connection);
                return (
                  <div key={connection.id} className="flex items-center justify-between p-3 hover:bg-muted/50 rounded-lg transition-colors">
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarImage src={connectionUser.avatar_url} />
                        <AvatarFallback>
                          {connectionUser.full_name?.[0] || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{connectionUser.full_name}</p>
                        <p className="text-sm text-muted-foreground">{connectionUser.title}</p>
                        <p className="text-xs text-muted-foreground">
                          Connected {format(new Date(connection.created_at), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
              {connections.length >= 5 && (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/dashboard/connections')}
                >
                  View All Connections
                </Button>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}